import { Link } from 'react-router';
import { SEO, BreadcrumbSchema, FAQSchema, ServiceSchema, SpeakableSchema } from '@/components/seo/seo';

function RatingBadge({ rating, size = 'md' }: { rating: 'red' | 'amber' | 'green'; size?: 'sm' | 'md' | 'lg' }) {
  const colors = {
    red: 'bg-rose-500 text-white',
    amber: 'bg-amber-500 text-white',
    green: 'bg-emerald-500 text-white',
  };
  const sizes = {
    sm: 'px-2 py-0.5 text-xs',
    md: 'px-3 py-1 text-sm',
    lg: 'px-4 py-2 text-base',
  };
  return (
    <span className={`inline-block font-bold rounded-full uppercase ${colors[rating]} ${sizes[size]}`}>
      {rating}
    </span>
  );
}

function QuoteCard({ quote, source }: { quote: string; source: string }) {
  return (
    <blockquote className="bg-surface-50 border-l-4 border-brand-500 p-6 rounded-r-xl">
      <p className="text-surface-800 italic">"{quote}"</p>
      <cite className="text-sm text-surface-600 mt-2 block not-italic">— {source}</cite>
    </blockquote>
  );
}

export function LambethCouncilPage() {
  return (
    <div className="min-h-screen">
      <SEO
        title="Lambeth Pothole Claims | One-Third of A-Roads in RED | Fixtyer"
        description="Lambeth Council rates one-third of its A-roads in RED condition and just 16% green, despite GREEN DfT spend scorecards. See what the AMBER rating means for your pothole claim under Section 58."
        canonical="/councils/lambeth"
      />
      <BreadcrumbSchema
        items={[
          { name: 'Home', url: '/' },
          { name: 'Councils', url: '/councils' },
          { name: 'Lambeth', url: '/councils/lambeth' },
        ]}
      />
      <FAQSchema
        faqs={[
          {
            question: 'Lambeth projects £9.3m capital spend against a £585,000 DfT allocation — can I still claim?',
            answer: 'Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect and road condition — and Lambeth is AMBER overall. Section 58 depends on whether the pothole that damaged your vehicle was reasonably inspected and repaired, not on aggregate spend. Lambeth\'s own report admits A-roads in RED condition rose 10% in five years while good-rated A-roads fell 19% since 2023.',
          },
          {
            question: 'What if my pothole was on a residential or unclassified road?',
            answer: 'U-roads make up 264km — 75% of Lambeth\'s 354km network. At the last biennial survey in 2024, 9.74% were RED and 55.58% were AMBER, with only 34.66% green — a 12% decline in good-rated U-roads since 2020. Condition data is collected boroughwide only every two years, so ask when your street was last surveyed.',
          },
          {
            question: 'U-road RED fell from 20% to 10% — does that weaken my claim?',
            answer: 'Not necessarily. The council attributes the shift partly to preventative micro-asphalt treatments on lower-trafficked roads, while A and B/C classified roads continue to decline. More than half of U-roads are now AMBER (sub-optimal), not green. A pothole claim still turns on the specific defect, prior reports, and whether a biennial survey regime was reasonable for that street.',
          },
          {
            question: 'Does the biennial condition survey gap affect Section 58?',
            answer: 'It can. Lambeth collects condition data through a boroughwide survey every two years, and published figures repeat in alternate years — suggesting no fresh network-level data in between. For a defect forming between survey years, the council must still show its routine inspection regime was adequate. Prior reports and photos matter more when network condition data has a two-year lag.',
          },
          {
            question: 'S81 notices to utility companies hit 829 in 2024/25 — does that help my claim?',
            answer: 'Potentially. Lambeth issued 829 Section 81 notices under NRSWA 1991 in 2024/25 — up from 324 in 2019/20 — compelling statutory undertakers to repair defective street apparatus. If your pothole sat near a recent utility excavation or damaged cover, third-party reinstatement failure may be relevant. The council still owes a duty under Section 41 of the Highways Act 1980 for the road surface itself.',
          },
        ]}
      />
      <ServiceSchema
        councilName="Lambeth Council"
        councilSlug="lambeth"
        rating="amber"
      />
      <SpeakableSchema
        url="/councils/lambeth"
        headline="Lambeth Road Maintenance Report"
        cssSelectors={['[data-speakable="summary"]', 'h1', 'h2']}
      />

      {/* Hero */}
      <section className="relative bg-brand-950 text-white overflow-hidden">
        {/* Top chevrons */}
        <div
          className="absolute top-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #f97316,
              #f97316 40px,
              #1e40af 40px,
              #1e40af 80px
            )`
          }}
        />

        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 pt-20 pb-16">
          <nav className="text-sm text-brand-400 mb-8 flex items-center gap-2">
            <Link to="/" className="hover:text-white transition-colors">Home</Link>
            <span>/</span>
            <Link to="/councils" className="hover:text-white transition-colors">Councils</Link>
            <span>/</span>
            <span className="text-white">Lambeth</span>
          </nav>

          <div className="flex flex-wrap items-center gap-3 mb-4">
            <RatingBadge rating="amber" size="lg" />
            <span className="text-brand-300">Overall</span>
            <span className="text-brand-500">|</span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Condition</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="green" size="sm" /> <span className="text-brand-300 text-sm">Spend</span>
            </span>
            <span className="flex items-center gap-2">
              <RatingBadge rating="amber" size="sm" /> <span className="text-brand-300 text-sm">Best Practice</span>
            </span>
          </div>

          <h1 className="font-display text-4xl sm:text-5xl font-bold leading-tight mb-6">
            Lambeth: One-Third of A-Roads in RED, Just 16% Green
          </h1>

          <p className="text-xl text-brand-200 max-w-2xl mb-8 leading-relaxed" data-speakable="summary">
            Lambeth earns <strong className="text-emerald-400">GREEN</strong> for spend — projecting
            <strong className="text-emerald-400"> £9.3m</strong> in capital works against a
            <strong className="text-emerald-400"> £585,000</strong> DfT allocation. Yet the overall rating is
            <strong className="text-amber-400"> AMBER</strong>, and the council's own report admits its most
            trafficked roads are collapsing: at the 2024 survey,
            <strong className="text-rose-400"> 33.18% of A-roads were in RED condition</strong> while only
            <strong className="text-rose-400"> 16.46% remained green</strong> — on a 354km network where
            75% of carriageway is residential streets surveyed only every other year.
          </p>

          {/* Key stat callout */}
          <div className="bg-rose-500/20 border border-rose-500/30 rounded-2xl p-6 max-w-md">
            <div className="text-6xl font-bold text-rose-400">33.18%</div>
            <div className="text-white font-medium mt-2">A-roads in RED condition (2024)</div>
            <div className="text-brand-300 text-sm mt-1">
              Up from 21.86% in 2020 across Lambeth's 44km of A-roads. The council itself reports a 10% rise
              in poor-rated main roads over five years and a 19% fall in green-rated A-roads since 2023.
            </div>
          </div>
        </div>

        {/* Bottom chevrons */}
        <div
          className="absolute bottom-0 left-0 right-0 h-8 z-10"
          style={{
            background: `repeating-linear-gradient(
              -45deg,
              #1e40af,
              #1e40af 40px,
              #f97316 40px,
              #f97316 80px
            )`
          }}
        />
      </section>

      {/* Network Overview */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            A Dense 354km Network
          </h2>
          <p className="text-surface-600 mb-8">
            Lambeth's own network breakdown — a compact inner-London borough where residential streets dominate
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Road class</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Length (km)</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Share of network</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">A roads</td>
                  <td className="px-6 py-4 text-center">44</td>
                  <td className="px-6 py-4 text-center">12.4%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-medium text-brand-900">B and C roads</td>
                  <td className="px-6 py-4 text-center">46</td>
                  <td className="px-6 py-4 text-center">13.0%</td>
                </tr>
                <tr className="border-b border-surface-100 bg-amber-50">
                  <td className="px-6 py-4 font-medium text-amber-900">U roads (unclassified)</td>
                  <td className="px-6 py-4 text-center font-bold">264</td>
                  <td className="px-6 py-4 text-center font-bold text-amber-700">74.6%</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Total roads</td>
                  <td className="px-6 py-4 text-center font-bold">354</td>
                  <td className="px-6 py-4 text-center font-bold">100%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid sm:grid-cols-3 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <div className="text-3xl font-bold text-brand-900">710 km</div>
              <div className="text-surface-600 text-sm mt-1">Footways maintained</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <div className="text-3xl font-bold text-brand-900">29.3 km</div>
              <div className="text-surface-600 text-sm mt-1">Cycleways</div>
            </div>
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <div className="text-3xl font-bold text-brand-900">13,675</div>
              <div className="text-surface-600 text-sm mt-1">Lit street assets (12,718 streetlights)</div>
            </div>
          </div>
        </div>
      </section>

      {/* The Condition Picture */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            What The Condition Data Shows
          </h2>
          <p className="text-surface-600 mb-8">
            Five years of RAG condition data from Lambeth's own transparency report — main roads failing,
            residential streets sliding into amber
          </p>

          {/* A-roads */}
          <div className="bg-rose-50 border border-rose-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-rose-900 mb-4">A-roads (44km — 12.4% of network): deteriorating fast</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-rose-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-rose-100 border-b border-rose-200">
                    <th className="px-4 py-2 text-left font-semibold text-rose-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">21.86%</td>
                    <td className="px-4 py-2 text-center">46.16%</td>
                    <td className="px-4 py-2 text-center">31.98%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">27.03%</td>
                    <td className="px-4 py-2 text-center">37.29%</td>
                    <td className="px-4 py-2 text-center">35.64%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">27.03%</td>
                    <td className="px-4 py-2 text-center">37.29%</td>
                    <td className="px-4 py-2 text-center">35.64%</td>
                  </tr>
                  <tr className="border-b border-rose-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">33.18%</td>
                    <td className="px-4 py-2 text-center">49.68%</td>
                    <td className="px-4 py-2 text-center text-rose-600">16.46%</td>
                  </tr>
                  <tr className="bg-rose-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">33.18%</td>
                    <td className="px-4 py-2 text-center font-bold">49.68%</td>
                    <td className="px-4 py-2 text-center font-bold text-rose-600">16.46%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-rose-800 font-medium">
              More than four-fifths of Lambeth's A-roads are now amber or red. Good-rated main roads have
              halved since 2020 — from 31.98% to 16.46%.
            </p>
          </div>

          {/* B/C roads */}
          <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-amber-900 mb-4">B and C roads (46km — 13% of network): steady decline</h3>
            <div className="bg-white rounded-xl overflow-hidden border border-amber-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-amber-100 border-b border-amber-200">
                    <th className="px-4 py-2 text-left font-semibold text-amber-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">14.73%</td>
                    <td className="px-4 py-2 text-center">36.61%</td>
                    <td className="px-4 py-2 text-center">48.66%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">16.03%</td>
                    <td className="px-4 py-2 text-center">35.8%</td>
                    <td className="px-4 py-2 text-center">48.17%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">16.03%</td>
                    <td className="px-4 py-2 text-center">35.8%</td>
                    <td className="px-4 py-2 text-center">48.17%</td>
                  </tr>
                  <tr className="border-b border-amber-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">15.63%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">44.57%</td>
                    <td className="px-4 py-2 text-center">39.8%</td>
                  </tr>
                  <tr className="bg-amber-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center">15.63%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">44.57%</td>
                    <td className="px-4 py-2 text-center font-bold">39.8%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-amber-800 font-medium">
              Green-rated B/C roads have fallen from 48.66% to 39.8% — a 9% drop the council attributes to
              insufficient funding for robust resurfacing on classified roads.
            </p>
          </div>

          {/* U-roads */}
          <div className="bg-white border border-surface-200 rounded-2xl p-6 mb-6">
            <h3 className="font-semibold text-brand-900 mb-4">U-roads (264km — 75% of network): amber surge</h3>
            <div className="bg-surface-50 rounded-xl overflow-hidden border border-surface-200 mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-surface-100 border-b border-surface-200">
                    <th className="px-4 py-2 text-left font-semibold text-brand-900">Year</th>
                    <th className="px-4 py-2 text-center font-semibold text-rose-700">Red</th>
                    <th className="px-4 py-2 text-center font-semibold text-amber-700">Amber</th>
                    <th className="px-4 py-2 text-center font-semibold text-emerald-700">Green</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2020</td>
                    <td className="px-4 py-2 text-center">19.99%</td>
                    <td className="px-4 py-2 text-center">33.16%</td>
                    <td className="px-4 py-2 text-center">46.85%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2021</td>
                    <td className="px-4 py-2 text-center">17.14%</td>
                    <td className="px-4 py-2 text-center">36.9%</td>
                    <td className="px-4 py-2 text-center">45.94%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2022</td>
                    <td className="px-4 py-2 text-center">17.14%</td>
                    <td className="px-4 py-2 text-center">36.9%</td>
                    <td className="px-4 py-2 text-center">45.94%</td>
                  </tr>
                  <tr className="border-b border-surface-100">
                    <td className="px-4 py-2 font-mono">2023</td>
                    <td className="px-4 py-2 text-center">9.74%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">55.58%</td>
                    <td className="px-4 py-2 text-center">34.66%</td>
                  </tr>
                  <tr className="bg-brand-50">
                    <td className="px-4 py-2 font-mono font-bold">2024</td>
                    <td className="px-4 py-2 text-center">9.74%</td>
                    <td className="px-4 py-2 text-center font-bold text-amber-600">55.58%</td>
                    <td className="px-4 py-2 text-center font-bold">34.66%</td>
                  </tr>
                </tbody>
              </table>
            </div>
            <p className="text-surface-600 text-sm">
              RED U-roads fell, but more than half are now AMBER — sub-optimal, not acceptable. Good-rated
              residential streets dropped 12% since 2020. On 264km of local roads, that is roughly 26km still
              in RED and 147km in amber condition at the 2024 survey.
            </p>
          </div>

          {/* Spend context */}
          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-2xl font-bold mb-4">GREEN Spend, AMBER Roads</h3>
            <div className="grid sm:grid-cols-3 gap-6 mb-6">
              <div>
                <div className="text-3xl font-bold text-emerald-400">£585,000</div>
                <div className="text-brand-300 text-sm">DfT capital allocation 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">£9.3m</div>
                <div className="text-brand-300 text-sm">Projected capital spend 2025/26</div>
              </div>
              <div>
                <div className="text-3xl font-bold text-emerald-400">85%</div>
                <div className="text-brand-300 text-sm">Estimated preventative share</div>
              </div>
            </div>
            <p className="text-xl font-semibold text-white">
              Lambeth projects spending nearly <span className="text-emerald-400">16 times its DfT allocation</span> on
              capital highways works — yet A-roads in RED condition rose 10% in five years. The problem is not
              the budget line on paper. Heavily trafficked classified roads are deteriorating faster than
              resurfacing keeps pace.
            </p>
          </div>
        </div>
      </section>

      {/* Survey Methodology */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            The Biennial Survey Gap
          </h2>
          <p className="text-surface-600 mb-8">
            How Lambeth measures road condition — and what it does not capture between surveys
          </p>

          <div className="grid md:grid-cols-2 gap-6 mb-8">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">Boroughwide Survey Every Two Years</h3>
              <p className="text-surface-600 text-sm mb-3">
                Lambeth collects condition data through a boroughwide survey every two years. Assets are assessed
                with five condition grades, convertible to the traditional three-grade RAG status reported to
                the DfT. Published figures repeat in consecutive years — 2021 matches 2022, and 2023 matches 2024
                — indicating fresh network-level data only on alternate years.
              </p>
              <p className="text-surface-600 text-sm">
                The council uses an accredited supplier and reports it is well prepared for the forthcoming
                BSI PAS 2161 national methodology shift.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Routine Inspections vs Network Surveys</h3>
              <p className="text-amber-800 text-sm mb-3">
                Between formal surveys, the authority relies on routine and ad-hoc inspections. Carriageway and
                footway defects are repaired following either routine or ad-hoc inspections. For 2025/26, Lambeth
                states the highway network will be routinely inspected and defects risk-assessed before reactive
                repairs are arranged.
              </p>
              <p className="text-amber-800 text-sm">
                Section 58 turns on whether that inspection regime was reasonable for the specific road where
                your pothole formed — not on the boroughwide survey scorecard alone.
              </p>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">Why This Matters For Section 58</h3>
            <p className="text-brand-200 mb-4">
              To rely on the Section 58 defence, Lambeth must show it had a reasonable system for knowing the
              condition of its roads. On a network where 75% is unclassified residential streets, ask:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• When was your road last condition-surveyed — 2023/24 or an earlier cycle?</li>
              <li>• If 55.58% of U-roads were amber at the last survey, what was done about yours?</li>
              <li>• Did routine inspections catch the defect before it damaged your vehicle?</li>
              <li>• On an A-road where 83% are amber or red, was inspection frequency adequate for traffic volume?</li>
            </ul>
            <p className="text-white font-medium">
              A council cannot point to a biennial network survey and claim continuous knowledge of every
              defect forming in between.
            </p>
          </div>
        </div>
      </section>

      {/* Pothole Repairs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            2,709 Carriageway Potholes in Five Years
          </h2>
          <p className="text-surface-600 mb-8">
            Lambeth's published pothole-fill counts — structural binder and surface course repairs only
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left text-sm font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center text-sm font-semibold text-brand-900">Potholes filled</th>
                </tr>
              </thead>
              <tbody className="text-sm">
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2020 to 2021</td>
                  <td className="px-6 py-4 text-center">306</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2021 to 2022</td>
                  <td className="px-6 py-4 text-center">374</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2022 to 2023</td>
                  <td className="px-6 py-4 text-center">692</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2023 to 2024</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">729</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono text-brand-900">2024 to 2025</td>
                  <td className="px-6 py-4 text-center">608</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold text-brand-900">Five-year total</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">2,709</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">2025/26 Projection: 676 Potholes</h3>
              <p className="text-surface-600 text-sm">
                Lambeth estimates it will fill 676 carriageway potholes in 2025/26. That is roughly 1.9 per
                day across 354km — a modest count for inner London, but each repair confirms defects are
                forming and being treated reactively rather than prevented.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Structural Impairment Only</h3>
              <p className="text-amber-800 text-sm">
                The council categorises these repairs according to when binder and surface courses were
                structurally impaired. Surface defects below that threshold may be repaired but not counted in
                the DfT pothole table — so the published figures understate total defect activity.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Spending Detail */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Five Years of Highways Spending
          </h2>
          <p className="text-surface-600 mb-8">
            Capital, revenue, and the preventative-reactive split from Lambeth's own figures
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-4 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">DfT allocation</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Capital spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Revenue spend</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Preventative</th>
                  <th className="px-4 py-3 text-center font-semibold text-brand-900">Reactive</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2020 to 2021</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£4,573,912</td>
                  <td className="px-4 py-3 text-center">£1,450,380</td>
                  <td className="px-4 py-3 text-center">76%</td>
                  <td className="px-4 py-3 text-center">24%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2021 to 2022</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£6,268,950</td>
                  <td className="px-4 py-3 text-center">£1,462,755</td>
                  <td className="px-4 py-3 text-center">80%</td>
                  <td className="px-4 py-3 text-center">20%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2022 to 2023</td>
                  <td className="px-4 py-3 text-center">£0</td>
                  <td className="px-4 py-3 text-center">£6,562,201</td>
                  <td className="px-4 py-3 text-center">£1,531,180</td>
                  <td className="px-4 py-3 text-center">81%</td>
                  <td className="px-4 py-3 text-center">19%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2023 to 2024</td>
                  <td className="px-4 py-3 text-center">£180,000</td>
                  <td className="px-4 py-3 text-center">£6,000,000</td>
                  <td className="px-4 py-3 text-center">£1,833,863</td>
                  <td className="px-4 py-3 text-center">75%</td>
                  <td className="px-4 py-3 text-center">25%</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-4 py-3 font-mono">2024 to 2025</td>
                  <td className="px-4 py-3 text-center">£180,000</td>
                  <td className="px-4 py-3 text-center">£5,300,000</td>
                  <td className="px-4 py-3 text-center">£1,719,709</td>
                  <td className="px-4 py-3 text-center">75%</td>
                  <td className="px-4 py-3 text-center">25%</td>
                </tr>
                <tr className="bg-emerald-50">
                  <td className="px-4 py-3 font-mono font-bold">2025 to 2026 (projected)</td>
                  <td className="px-4 py-3 text-center font-bold">£585,000</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">£9,305,323</td>
                  <td className="px-4 py-3 text-center font-bold">£1,779,684</td>
                  <td className="px-4 py-3 text-center font-bold text-emerald-700">85%</td>
                  <td className="px-4 py-3 text-center font-bold">15%</td>
                </tr>
              </tbody>
            </table>
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-surface-50 rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">HAIP Capital Programme</h3>
              <p className="text-surface-600 text-sm">
                Lambeth primarily uses its Highways Asset Improvement Programme (HAIP) capital funding for
                resurfacing schemes on carriageways and footways. Some capital has also gone to the Golden
                Jubilee Footbridges. For 2025/26, the council plans to resurface 2.17 miles (3.5km) of
                carriageway and improve 18 footways.
              </p>
            </div>
            <div className="bg-emerald-50 border border-emerald-200 rounded-xl p-6">
              <h3 className="font-semibold text-emerald-900 mb-3">17:3 Preventative Ratio Planned</h3>
              <p className="text-emerald-800 text-sm">
                For 2025/26, Lambeth states the split between preventative and reactive works will be a ratio
                of 17:3. DfT funds are planned to contribute towards preventative treatments measuring up to
                4.2 miles in length, subject to feasibility and budget allocation.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Council Admissions */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Lambeth's Own Condition Admissions
          </h2>
          <p className="text-surface-600 mb-8">
            What the council says about deteriorating roads — in its own words
          </p>

          <div className="space-y-6 mb-8">
            <QuoteCard
              quote="The condition data highlights the deterioration of Lambeth's most trafficked roads. The percentage of A class roads that are in a poor (red) condition has increased by 10% over five years. Since 2023, there has been a 19% decrease of green (good) rated A class roads, demonstrating how heavily trafficked roads are the most susceptible to damage."
              source="Lambeth Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="B and C class roads are also steadily declining, with a 9% fall in roads rated as 'green' or good. Without funding for robust resurfacing schemes, classified roads (including A, B, & C) will deteriorate further, falling into a serviceable condition before ultimately becoming poor."
              source="Lambeth Council Local Highways Maintenance Transparency Report 2025"
            />
            <QuoteCard
              quote="Unclassified roads, which constitute the majority of Lambeth's network, are essential for local journeys. They have similarly deteriorated since 2020, showing a 12% decline in roads rated as 'good.' Preventative maintenance treatments (such as Micro Asphalt) on these lower trafficked roads could prevent costly future repairs and keep them in an acceptable condition."
              source="Lambeth Council Local Highways Maintenance Transparency Report 2025"
            />
          </div>

          <div className="grid md:grid-cols-2 gap-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-3">What These Admissions Mean</h3>
              <p className="text-surface-600 text-sm mb-3">
                Lambeth formally acknowledges that its classified roads are deteriorating and that without
                robust resurfacing funding, they will fall further into poor condition. That is documented
                knowledge of elevated risk on the borough's busiest routes.
              </p>
              <p className="text-surface-600 text-sm">
                Knowledge of accelerating wear on A-roads raises the standard for what a reasonable inspection
                frequency looks like under Section 58.
              </p>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-xl p-6">
              <h3 className="font-semibold text-amber-900 mb-3">Questions Worth Asking</h3>
              <ul className="text-amber-800 text-sm space-y-2">
                <li>• Was your pothole on an A-road where a third of the network is already RED?</li>
                <li>• Did the council increase inspection frequency as condition worsened?</li>
                <li>• If deterioration was known, why was the specific defect not caught before it caused damage?</li>
                <li>• Was micro-asphalt treatment planned for your residential street — and did it happen?</li>
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* S81 Notices */}
      <section className="py-16 bg-white">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            829 S81 Notices in 2024/25
          </h2>
          <p className="text-surface-600 mb-8">
            Third-party street apparatus failures — a rising enforcement workload Lambeth documents itself
          </p>

          <div className="bg-white rounded-2xl border border-surface-200 overflow-hidden mb-8">
            <table className="w-full text-sm">
              <thead>
                <tr className="bg-surface-100 border-b border-surface-200">
                  <th className="px-6 py-3 text-left font-semibold text-brand-900">Year</th>
                  <th className="px-6 py-3 text-center font-semibold text-brand-900">S81 notices issued</th>
                </tr>
              </thead>
              <tbody>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2019/20</td>
                  <td className="px-6 py-4 text-center">324</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2020/21</td>
                  <td className="px-6 py-4 text-center">542</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2021/22</td>
                  <td className="px-6 py-4 text-center">376</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2022/23</td>
                  <td className="px-6 py-4 text-center">291</td>
                </tr>
                <tr className="border-b border-surface-100">
                  <td className="px-6 py-4 font-mono">2023/24</td>
                  <td className="px-6 py-4 text-center">487</td>
                </tr>
                <tr className="bg-rose-50">
                  <td className="px-6 py-4 font-mono font-bold">2024/25</td>
                  <td className="px-6 py-4 text-center font-bold text-rose-600">829</td>
                </tr>
                <tr className="bg-brand-50">
                  <td className="px-6 py-4 font-bold">Total (six years)</td>
                  <td className="px-6 py-4 text-center font-bold">2,849</td>
                </tr>
              </tbody>
            </table>
          </div>

          <QuoteCard
            quote="When defective street apparatus—such as damaged covers —is identified, the Highways Service issues a Section 81 (S81) notice under NRSWA 1991. This compels the responsible statutory undertaker to inspect and repair the fault within a set timeframe. S81 notices support asset management by ensuring third-party infrastructure is safely maintained, reducing risk and shifting repair costs away from the authority."
            source="Lambeth Council Local Highways Maintenance Transparency Report 2025"
          />
        </div>
      </section>

      {/* What AMBER + GREEN spend means for claims */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-3xl font-bold text-brand-950 mb-4">
            Claiming Against a Well-Funded AMBER Council
          </h2>
          <p className="text-surface-600 mb-8">
            Honest assessment: Lambeth invests heavily — here's how that changes your approach
          </p>

          <div className="grid md:grid-cols-2 gap-8 mb-8">
            <div className="bg-white rounded-2xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-4">What Works In The Council's Favour</h3>
              <ul className="text-surface-700 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> GREEN spend scorecard — projects nearly 16 times its DfT capital allocation
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Up to 85% preventative maintenance projected for 2025/26
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Accredited condition survey supplier, PAS 2161-ready
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> Risk-based HAIP points system for planned resurfacing
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-emerald-500">✓</span> U-road RED share fell from 19.99% to 9.74% at last survey
                </li>
              </ul>
              <p className="text-surface-500 text-sm mt-4 italic">
                Expect a documented Section 58 defence on well-maintained stretches. Generic claims will struggle.
              </p>
            </div>

            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <h3 className="font-semibold text-amber-900 mb-4">What Works In Yours</h3>
              <ul className="text-amber-800 space-y-2 text-sm">
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> AMBER condition — one-third of A-roads in RED, only 16.46% green
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Council admits 10% rise in poor A-roads and 9% fall in good B/C roads
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 55.58% of U-roads in amber — sub-optimal on 264km of residential streets
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> Boroughwide condition surveys only every two years
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> 2,709 pothole repairs in five years — defects still forming reactively
                </li>
                <li className="flex items-start gap-2">
                  <span className="text-rose-500">✗</span> S81 notices tripled since 2019/20 — utility damage risk rising
                </li>
              </ul>
            </div>
          </div>

          <div className="bg-brand-950 text-white rounded-2xl p-8">
            <h3 className="font-display text-xl font-bold mb-4">The Winning Strategy Here Is Specificity</h3>
            <p className="text-brand-200 mb-4">
              Against a council with GREEN spend scorecards and a risk-based asset programme, your claim lives
              or dies on the <strong className="text-white">specific defect</strong>:
            </p>
            <ul className="text-brand-300 space-y-2 mb-6">
              <li>• Prior reports of the same pothole (FixMyStreet, council reports) — proof of actual notice</li>
              <li>• Photos showing the defect's size, depth and visible age (weathered edges, previous patching)</li>
              <li>• The road's class — on an A-road, cite the 33.18% RED network rate; on a U-road, the biennial survey gap</li>
              <li>• Proximity to recent utility works or S81-enforced repairs</li>
            </ul>
            <p className="text-white font-medium">
              Mac builds exactly this case: he searches for prior reports, assesses your photo evidence, and
              cites Lambeth's own transparency data where it helps you.
            </p>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 bg-brand-950 text-white">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-display text-3xl font-bold mb-4">
            Hit a Pothole in Lambeth?
          </h2>
          <p className="text-xl text-brand-200 mb-8">
            A well-funded council demands a well-built claim. £35 for a professional claim pack.
          </p>

          <div className="grid sm:grid-cols-2 gap-6 mb-8">
            <div className="bg-brand-900/50 rounded-xl p-6 border border-brand-800">
              <h3 className="font-semibold text-brand-300 mb-3">DIY Claim</h3>
              <ul className="text-sm text-brand-400 space-y-1 text-left">
                <li>• Submit photos and invoices</li>
                <li>• Use generic template letter</li>
                <li>• No A-road deterioration argument</li>
                <li>• No biennial survey-gap analysis</li>
                <li>• No S81 utility-works check</li>
              </ul>
            </div>
            <div className="bg-accent-500/20 rounded-xl p-6 border border-accent-500/30">
              <h3 className="font-semibold text-white mb-3">Professional Claim Pack</h3>
              <ul className="text-sm text-brand-200 space-y-1 text-left">
                <li>✅ 33.18% A-road RED rate documented</li>
                <li>✅ Biennial survey gap argued</li>
                <li>✅ 2,709 pothole repairs in five years cited</li>
                <li>✅ Prior reports searched and attached</li>
                <li>✅ Section 58 rebuttal tailored to Lambeth</li>
              </ul>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <Link
              to="/check?council=lambeth"
              className="inline-flex items-center justify-center px-8 py-4 bg-accent-500 text-white font-semibold rounded-xl hover:bg-accent-600 transition-all hover:shadow-lg hover:shadow-accent-500/25"
            >
              Order My Lambeth Claim Pack — £35
              <svg className="ml-2 w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 8l4 4m0 0l-4 4m4-4H3" />
              </svg>
            </Link>
            <Link
              to="/road-maintenance-report"
              className="inline-flex items-center justify-center px-8 py-4 bg-white/10 text-white font-semibold rounded-xl hover:bg-white/20 transition-colors"
            >
              View All Council Ratings
            </Link>
          </div>

          <p className="text-brand-400 text-sm mt-6">
            No percentage fees. You keep 100% of any compensation.
          </p>
        </div>
      </section>

      {/* FAQs */}
      <section className="py-16 bg-surface-50">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <h2 className="font-display text-2xl font-bold text-brand-950 mb-8">
            Frequently Asked Questions
          </h2>

          <div className="space-y-6">
            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Lambeth projects £9.3m capital spend against a £585,000 DfT allocation — can I still claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Yes. The DfT Spend scorecard is GREEN, but your claim turns on the specific defect and road
                condition — and Lambeth is AMBER overall. Section 58 depends on whether the pothole that
                damaged your vehicle was reasonably inspected and repaired, not on aggregate spend. Lambeth's
                own report admits A-roads in RED condition rose 10% in five years while good-rated A-roads
                fell 19% since 2023.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                What if my pothole was on a residential or unclassified road?
              </h3>
              <p className="text-surface-600 text-sm">
                U-roads make up 264km — 75% of Lambeth's 354km network. At the last biennial survey in 2024,
                9.74% were RED and 55.58% were AMBER, with only 34.66% green — a 12% decline in good-rated
                U-roads since 2020. Condition data is collected boroughwide only every two years, so ask when
                your street was last surveyed.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                U-road RED fell from 20% to 10% — does that weaken my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Not necessarily. The council attributes the shift partly to preventative micro-asphalt treatments
                on lower-trafficked roads, while A and B/C classified roads continue to decline. More than half
                of U-roads are now AMBER (sub-optimal), not green. A pothole claim still turns on the specific
                defect, prior reports, and whether a biennial survey regime was reasonable for that street.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                Does the biennial condition survey gap affect Section 58?
              </h3>
              <p className="text-surface-600 text-sm">
                It can. Lambeth collects condition data through a boroughwide survey every two years, and
                published figures repeat in alternate years — suggesting no fresh network-level data in between.
                For a defect forming between survey years, the council must still show its routine inspection
                regime was adequate. Prior reports and photos matter more when network condition data has a
                two-year lag.
              </p>
            </div>

            <div className="bg-white rounded-xl p-6 border border-surface-200">
              <h3 className="font-semibold text-brand-900 mb-2">
                S81 notices to utility companies hit 829 in 2024/25 — does that help my claim?
              </h3>
              <p className="text-surface-600 text-sm">
                Potentially. Lambeth issued 829 Section 81 notices under NRSWA 1991 in 2024/25 — up from 324 in
                2019/20 — compelling statutory undertakers to repair defective street apparatus. If your pothole
                sat near a recent utility excavation or damaged cover, third-party reinstatement failure may be
                relevant. The council still owes a duty under Section 41 of the Highways Act 1980 for the road
                surface itself.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Attribution Footer */}
      <section className="py-8 bg-surface-100 border-t border-surface-200">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8">
          <p className="text-xs text-surface-500 text-center">
            Data sources: <a
              href="https://www.gov.uk/government/publications/local-road-maintenance-ratings/local-road-maintenance-ratings-2025-to-2026"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Department for Transport — Local Road Maintenance Ratings 2025 to 2026
            </a> | <a
              href="https://www.lambeth.gov.uk/sites/default/files/2025-06/Lambeth%20-Highways%20Maintenance%20Transparency%20Report%20-2025%20-2026.pdf"
              target="_blank"
              rel="noopener noreferrer"
              className="text-brand-600 hover:underline"
            >
              Lambeth Council Local Highways Maintenance Transparency Report 2025/2026 (June 2025)
            </a>.
            Contains public sector information licensed under the{' '}
            <a href="https://www.nationalarchives.gov.uk/doc/open-government-licence/version/3/" target="_blank" rel="noopener noreferrer" className="text-brand-600 hover:underline">
              Open Government Licence v3.0
            </a>.
          </p>
        </div>
      </section>
    </div>
  );
}
